/**
 * Click-insert rules shared by the sidebar palette and the Cmd+K palette
 * (extracted from the phase 2/3 outline picker). Sections insert at the
 * content root, Columns need a Section context, everything else lands
 * after the selection. Successful inserts feed the recents history.
 */

import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { COLUMNS_SLUG, SECTION_SLUG } from './nestingRules';
import { isSharedBlock, markerId } from './types';
import type { BlockNode } from './types';
import { useBuilderStore } from './useBuilderStore';
import { useBuilderRegistry } from './useBuilderRegistry';
import { usePaletteHistory } from './usePaletteHistory';

export interface BlockInsertion {
    /** Root Section the selection lives in — the click-insert target for Columns. */
    sectionContextId: ComputedRef<string | null>;
    canInsertType(slug: string): boolean;
    insertTypeBlock(slug: string): string | null;
    insertSharedBlock(id: string | number): string | null;
}

/** New shared-block reference node — the render ignores value entirely. */
export function sharedBlockNode(id: string | number): BlockNode {
    return { type: id, useBlock: true, value: null, settings: {} };
}

export function useBlockInsertion(): BlockInsertion {
    const store = useBuilderStore();
    const registry = useBuilderRegistry();
    const history = usePaletteHistory();

    const containsId = (block: BlockNode, id: string): boolean => {
        if (markerId(block) === id) {
            return true;
        }

        return (block.children ?? []).some((child) => containsId(child, id));
    };

    const sectionContextId = computed<string | null>(() => {
        const selected = store.selectedId.value;

        if (selected === null) {
            return null;
        }

        for (const block of store.content.value) {
            if (isSharedBlock(block) || block.type !== SECTION_SLUG) {
                continue;
            }

            if (containsId(block, selected)) {
                return markerId(block);
            }
        }

        return null;
    });

    const newTypeBlock = (slug: string): BlockNode => ({
        type: slug,
        useBlock: false,
        value: registry.defaultValueFor(slug),
        settings: {},
    });

    return {
        sectionContextId,
        canInsertType(slug) {
            return slug !== COLUMNS_SLUG || sectionContextId.value !== null;
        },
        insertTypeBlock(slug) {
            let inserted: string | null = null;

            if (slug === SECTION_SLUG) {
                inserted = store.insertAt(newTypeBlock(slug), null, 0, null);
            } else if (slug === COLUMNS_SLUG) {
                if (sectionContextId.value === null) {
                    return null;
                }

                inserted = store.insertAt(newTypeBlock(slug), sectionContextId.value, 0, null);
            } else {
                inserted = store.insertAfter(store.selectedId.value, newTypeBlock(slug));
            }

            if (inserted !== null) {
                history.pushRecent({ kind: 'type', key: slug });
            }

            return inserted;
        },
        insertSharedBlock(id) {
            const inserted = store.insertAfter(store.selectedId.value, sharedBlockNode(id));

            if (inserted !== null) {
                history.pushRecent({ kind: 'shared', key: String(id) });
            }

            return inserted;
        },
    };
}
